import { useState, useEffect } from 'react';

/**
 * A hook that returns whether the window matches the given media query.
 * @param query The media query to match against, e.g. '(max-width: 768px)'
 * @returns A boolean indicating whether the media query matches
 */
export const useMediaQuery = (query: string): boolean => {
  // Initialize with the current match state
  const getMatches = (): boolean => {
    // Check if window is defined (to avoid SSR issues)
    if (typeof window !== 'undefined') {
      return window.matchMedia(query).matches;
    }
    return false;
  };

  const [matches, setMatches] = useState<boolean>(getMatches());

  useEffect(() => {
    // Get initial value
    const mediaQuery = window.matchMedia(query);
    setMatches(mediaQuery.matches);

    // Define a callback function to handle changes
    const handleChange = (event: MediaQueryListEvent) => {
      setMatches(event.matches);
    };

    // Add the callback as a listener for changes to the media query
    mediaQuery.addEventListener('change', handleChange);

    // Clean up
    return () => {
      mediaQuery.removeEventListener('change', handleChange);
    };
  }, [query]);

  return matches;
}; 